// POST /api/v1/admin/portfolios/:portfolio_id/close-recruiting —
// recruiting → composed once the roster meets the min-creators floor
// (non-withdrawn only). Unlocks drop release for recruited portfolios.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type CloseRecruitingOutcome = {
  portfolio_id: string;
  recruiting_status: string;
  creator_count: number;
};

export class CloseRecruitingError extends Error {
  code: string;
  min?: number;
  provided?: number;
  constructor(code: string, message: string, min?: number, provided?: number) {
    super(message);
    this.code = code;
    this.min = min;
    this.provided = provided;
  }
}

async function closeRecruiting(input: {
  portfolio_id: string;
}): Promise<CloseRecruitingOutcome> {
  const response = await fetch(
    `/api/v1/admin/portfolios/${input.portfolio_id}/close-recruiting`,
    {
      method: "POST",
      credentials: "include",
      // /api/v1/admin/portfolios is a FINANCIAL_MUTATION_PREFIX —
      // POSTs without this header 400 (IDEMPOTENCY_KEY_REQUIRED).
      headers: { "x-idempotency-key": crypto.randomUUID() },
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Close recruiting failed (HTTP ${response.status})`;
    let min: number | undefined;
    let provided: number | undefined;
    try {
      const body = (await response.json()) as {
        error?: {
          code?: string;
          message?: string;
          min?: number;
          provided?: number;
        };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
      min = body.error?.min;
      provided = body.error?.provided;
    } catch {
      // non-JSON body
    }
    throw new CloseRecruitingError(code, message, min, provided);
  }
  const body = (await response.json()) as { data: CloseRecruitingOutcome };
  return body.data;
}

export function useCloseRecruiting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: closeRecruiting,
    onSuccess: (outcome) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio", outcome.portfolio_id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-portfolios"] });
    },
    onError: (error) => {
      if (error instanceof CloseRecruitingError) {
        if (error.code === "PORTFOLIO_INSUFFICIENT_CREATORS") {
          toast.error(
            `Roster too small to close: ${error.provided ?? "?"} active creator(s), need ${error.min ?? "?"}.`,
          );
          return;
        }
        toast.error(error.message);
      } else {
        toast.error("Something went wrong. Try again.");
      }
    },
  });
}
